import { useMemo, useState, type FormEvent } from "react";
import FormFeedback from "@/components/base/FormFeedback";
import {
  submitLead,
  isApiConfiguredCheck,
  getSubcategories,
} from "@/services/directoryService";
import { getPlanLabel, resolvePlanLimits } from "@/utils/plans";
import type { Plan } from "@/types/plans";
import type { Subcategory } from "@/types/directory";

interface CategoryOption {
  slug: string;
  name: string;
}

interface ListBusinessFormProps {
  categories: CategoryOption[];
  districts: { slug: string; name: string }[];
  plans: Plan[];
  defaultPlan: string;
}

const endpoint = "https://readdy.ai/api/form/dak3pgrbcsf0ijchd4g0";
const HONEYPOT = "website_alt";

interface PlanChoice {
  slug: string;
  name: string;
  priceYearly: number;
  tagline: string;
  icon: string;
  highlights: string[];
}

/** Minimal plan choices used only if the plans API returns nothing. */
const fallbackPlans: PlanChoice[] = [
  { slug: "free",                name: "Free",              priceYearly: 0, tagline: "Basic listing, free forever.",       icon: "ri-store-3-line",     highlights: ["List in 1 Category", "Up to 3 photos"] },
  { slug: "business",            name: "Business",          priceYearly: 599, tagline: "For growing businesses.",             icon: "ri-rocket-2-line",    highlights: ["List in One Main Category and 4 Sub Categories"] },
  { slug: "premium",             name: "Premium",           priceYearly: 1599, tagline: "Maximum visibility & video.",        icon: "ri-vip-crown-2-line", highlights: ["List in 3 Main Categories and 6 Sub Categories", "Add a video tour"] },
  { slug: "featured-listing",    name: "Featured Listing",  priceYearly: 0, tagline: "Business features + featured placement.", icon: "ri-star-smile-line",  highlights: ["1 main category + 4 sub categories"] },
  { slug: "sponsored-listing",   name: "Sponsored Listing", priceYearly: 0, tagline: "Premium features + top sponsored placement.", icon: "ri-megaphone-line",   highlights: ["3 main categories + 6 sub categories", "Video tour enabled"] },
];

/**
 * Values already chosen in the OTHER slots of the same group. Used to hide
 * options that are already in use, so the same category can never be picked
 * twice (the value currently held by this slot always stays selectable).
 */
function takenElsewhere(values: string[], index: number): Set<string> {
  const taken = new Set<string>();
  values.forEach((value, i) => {
    if (i !== index && value) taken.add(value);
  });
  return taken;
}

const inputClass =
  "h-11 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";
const selectClass = `${inputClass} cursor-pointer appearance-none`;
const labelClass =
  "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600";

export default function ListBusinessForm({
  categories,
  districts,
  plans,
  defaultPlan,
}: ListBusinessFormProps) {
  const planChoices: PlanChoice[] = plans.length
    ? plans.map((p) => ({
        slug: p.slug,
        name: p.name,
        priceYearly: p.priceYearly,
        tagline: p.tagline,
        icon: p.icon,
        highlights: p.highlights ?? [],
      }))
    : fallbackPlans;

  // Category allowances are read straight from the chosen plan's own content,
  // so whatever numbers the admin writes on a plan drive the form automatically.
  const initialLimits = resolvePlanLimits(
    planChoices.find((p) => p.slug === defaultPlan) ?? { slug: defaultPlan },
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const [planSlug, setPlanSlug] = useState(defaultPlan);
  const [mainCategories, setMainCategories] = useState<string[]>(() =>
    Array(initialLimits.categories).fill(""),
  );
  const [subCategories, setSubCategories] = useState<string[]>(() =>
    Array(initialLimits.subcategories).fill(""),
  );
  const [subPool, setSubPool] = useState<Record<string, Subcategory[]>>({});

  const selectedPlan = planChoices.find((p) => p.slug === planSlug);
  const limits = resolvePlanLimits(selectedPlan ?? { slug: planSlug });
  const planLabel = selectedPlan?.name ?? getPlanLabel(planSlug);

  /** The first available plan whose copy grants a video tour, used by the nudge. */
  const videoPlan = useMemo(
    () => planChoices.find((p) => resolvePlanLimits(p).video),
    [planChoices],
  );

  const subOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: Subcategory[] = [];
    for (const slug of mainCategories) {
      for (const sub of subPool[slug] ?? []) {
        if (seen.has(sub.slug)) continue;
        seen.add(sub.slug);
        out.push(sub);
      }
    }
    return out;
  }, [mainCategories, subPool]);

  /** The first chosen category is the primary one — the only compulsory pick. */
  const primaryCategoryName = useMemo(
    () => categories.find((c) => c.slug === mainCategories[0])?.name ?? "",
    [categories, mainCategories],
  );

  function selectPlan(slug: string) {
    const next = resolvePlanLimits(planChoices.find((p) => p.slug === slug) ?? { slug });
    setPlanSlug(slug);
    setMainCategories(Array(next.categories).fill(""));
    setSubCategories(Array(next.subcategories).fill(""));
  }

  async function loadSubcategories(categorySlug: string) {
    if (!categorySlug || subPool[categorySlug]) return;
    try {
      const subs = await getSubcategories(categorySlug);
      setSubPool((prev) => ({ ...prev, [categorySlug]: subs }));
    } catch {
      setSubPool((prev) => ({ ...prev, [categorySlug]: [] }));
    }
  }

  function setMainCategory(index: number, value: string) {
    setMainCategories((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setSubCategories(Array(limits.subcategories).fill(""));
    void loadSubcategories(value);
  }

  function setSubCategory(index: number, value: string) {
    setSubCategories((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = formData.get(HONEYPOT);
    if (typeof honeypotValue === "string" && honeypotValue.trim().length > 0) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete(HONEYPOT);

    setStatus("submitting");
    setErrorMessage("");

    const categoryNames = mainCategories
      .filter(Boolean)
      .map((slug) => categories.find((c) => c.slug === slug)?.name ?? slug);
    const subNames = subCategories
      .filter(Boolean)
      .map((slug) => subOptions.find((s) => s.slug === slug)?.name ?? slug);

    // ── When backend is live: submit to POST /leads ───────────────────────────
    if (isApiConfiguredCheck()) {
      try {
        const name    = String(formData.get("owner_name") ?? "");
        const email   = String(formData.get("email") ?? "");
        const phone   = String(formData.get("phone") ?? "");
        const subject = `New listing: ${String(formData.get("business_name") ?? "")}`;
        const parts   = [
          `Business: ${formData.get("business_name")}`,
          `Plan: ${planLabel}`,
          `Primary category: ${primaryCategoryName || "—"}`,
          `Category: ${categoryNames.join(", ") || "—"}`,
          subNames.length ? `Sub categories: ${subNames.join(", ")}` : null,
          `District: ${formData.get("district")}`,
          `Town: ${formData.get("town")}`,
          `Address: ${formData.get("address")}`,
          `Website: ${formData.get("website_url") || "N/A"}`,
          limits.video ? `Video tour: ${formData.get("video_url") || "N/A"}` : null,
          `Description: ${formData.get("description")}`,
        ].filter(Boolean);
        await submitLead({
          formType:   "listing",
          name,
          email:      email || undefined,
          phone:      phone || undefined,
          subject,
          message:    parts.join(" | "),
          sourcePage: "/list-your-business",
        });
        setStatus("success");
        form.reset();
        return;
      } catch {
        // fall through to platform form as backup
      }
    }

    // ── Fallback / demo: submit to Readdy platform form ───────────────────────
    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string") body.append(key, value);
      });
      body.append("category", categoryNames.join(", "));
      if (subNames.length) body.append("sub_categories", subNames.join(", "));

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam    = String(serverMsg).toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMessage(String(serverMsg).trim() || "Something went wrong. Please try again in a moment.");
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "We could not reach the server. Please check your connection and try again.",
      );
    }
  };

  if (status === "success") {
    return (
      <FormFeedback
        status="success"
        title="Application received"
        message="Thank you! Our team will review your business details and get in touch within 1–2 working days to activate your listing."
      />
    );
  }

  return (
    <form id="list-business-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {/* ── Step 1: Plan ─────────────────────────────────────────────────────── */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[11px] font-bold text-background-50">
            1
          </span>
          <h2 className="font-heading text-lg font-bold text-foreground-950">Choose your plan</h2>
        </div>
        <p className="mt-1.5 text-sm text-foreground-600">
          Your plan decides how many categories you can list under and whether you can add a video
          tour. You can change or upgrade any time.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {planChoices.map((option) => {
            const selected = option.slug === planSlug;
            return (
              <button
                key={option.slug}
                type="button"
                onClick={() => selectPlan(option.slug)}
                aria-pressed={selected}
                className={`flex cursor-pointer flex-col rounded-lg border p-4 text-left transition-colors duration-200 ${
                  selected
                    ? "border-primary-400 bg-primary-50 ring-1 ring-primary-200"
                    : "border-background-300 bg-background-50 hover:border-primary-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-md ${
                      selected ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600"
                    }`}
                  >
                    <i className={`${option.icon} text-lg`} />
                  </span>
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                      selected ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-300"
                    }`}
                  >
                    {selected ? <i className="ri-check-line text-xs" /> : null}
                  </span>
                </div>
                <p className="mt-3 font-heading text-sm font-bold text-foreground-950">{option.name}</p>
                <p className="mt-0.5 font-heading text-lg font-extrabold text-foreground-950">
                  {option.priceYearly === 0 ? "Free" : `₹${option.priceYearly.toLocaleString("en-IN")}`}
                  {option.priceYearly === 0 ? null : (
                    <span className="ml-1 text-xs font-medium text-foreground-500">/year</span>
                  )}
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{option.tagline}</p>
              </button>
            );
          })}
        </div>

        <input type="hidden" name="plan" value={planLabel} />
      </div>

      <div className="h-px w-full bg-background-200" />

      {/* ── Step 2: Business details ─────────────────────────────────────────── */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[11px] font-bold text-background-50">
            2
          </span>
          <h2 className="font-heading text-lg font-bold text-foreground-950">Business details</h2>
        </div>
        <p className="mt-1.5 text-sm text-foreground-600">Fields marked with an asterisk are required.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="owner-name">
            Owner / contact name *
          </label>
          <input
            id="owner-name"
            name="owner_name"
            type="text"
            required
            autoComplete="name"
            placeholder="e.g. Shrinivas Kamath"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="business-name">
            Business name *
          </label>
          <input
            id="business-name"
            name="business_name"
            type="text"
            required
            placeholder="e.g. Kamath Hardware"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="business-email">
            Email *
          </label>
          <input
            id="business-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="business-phone">
            Phone *
          </label>
          <input
            id="business-phone"
            name="phone"
            type="tel"
            required
            autoComplete="tel"
            placeholder="+91 98450 00000"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="business-whatsapp">
            WhatsApp number
          </label>
          <input
            id="business-whatsapp"
            name="whatsapp"
            type="tel"
            placeholder="+91 98450 00000"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="business-website">
            Website
          </label>
          <input
            id="business-website"
            name="website_url"
            type="url"
            placeholder="https://yourbusiness.in"
            className={inputClass}
          />
        </div>
      </div>

      {/* ── Categories & sub categories (plan-driven) ─────────────────────────── */}
      <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-sm font-bold text-foreground-950">
            Categories &amp; sub categories
          </h3>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-semibold text-primary-800">
            <i className="ri-vip-crown-2-line text-xs" />
            {planLabel} plan
          </span>
        </div>
        <p className="mt-1.5 text-xs text-foreground-600">
          Your plan lets you pick{" "}
          <strong className="font-semibold text-foreground-800">
            {limits.categories} {limits.categories === 1 ? "main category" : "main categories"}
          </strong>
          {limits.subcategories > 0 ? (
            <>
              {" "}and{" "}
              <strong className="font-semibold text-foreground-800">
                {limits.subcategories} sub {limits.subcategories === 1 ? "category" : "categories"}
              </strong>
            </>
          ) : (
            <> (sub categories come with paid plans)</>
          )}
          .
        </p>
        <p className="mt-1 text-xs text-foreground-500">
          Choices you have already picked are hidden from the other dropdowns, so you cannot select
          the same category twice.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-600">
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-image-line text-sm text-primary-600" />
            </span>
            Up to <strong className="font-semibold text-foreground-800">{limits.photos}</strong> photos
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center">
              <i
                className={
                  limits.video
                    ? "ri-video-line text-sm text-primary-600"
                    : "ri-video-off-line text-sm text-foreground-400"
                }
              />
            </span>
            Video tour {limits.video ? "included" : "not included"}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {mainCategories.map((value, index) => (
            <div key={`main-${index}`}>
              <label className={labelClass} htmlFor={`business-category-${index}`}>
                {index === 0 ? "Primary category" : `Additional category ${index + 1}`}
                {index === 0 ? " *" : ""}
              </label>
              <select
                id={`business-category-${index}`}
                name={`category_${index + 1}`}
                required={index === 0}
                value={value}
                onChange={(event) => setMainCategory(index, event.target.value)}
                className={selectClass}
              >
                <option value="" disabled>
                  {index === 0 ? "Select category" : "Select additional category"}
                </option>
                {categories
                  .filter((category) => !takenElsewhere(mainCategories, index).has(category.slug))
                  .map((category) => (
                    <option key={category.slug} value={category.slug}>
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-foreground-600">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-information-line text-sm text-primary-600" />
          </span>
          <span>
            Your first choice is your{" "}
            <strong className="font-semibold text-foreground-800">primary category</strong> and the
            only compulsory one. If your paid plan expires and is not renewed within 10 days, the
            listing drops back to the Free plan and stays listed under this primary category.
          </span>
        </div>
        <input type="hidden" name="primary_category" value={primaryCategoryName} />

        {limits.subcategories > 0 ? (
          <div className="mt-4 border-t border-background-200 pt-4">
            <p className={labelClass}>Sub categories</p>
            {subOptions.length === 0 ? (
              <p className="text-xs text-foreground-500">
                Pick a category above to choose from its sub categories.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {subCategories.map((value, index) => (
                  <select
                    key={`sub-${index}`}
                    id={`business-subcategory-${index}`}
                    aria-label={`Sub category ${index + 1}`}
                    name={`subcategory_${index + 1}`}
                    value={value}
                    onChange={(event) => setSubCategory(index, event.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select sub category {index + 1}</option>
                    {subOptions
                      .filter((sub) => !takenElsewhere(subCategories, index).has(sub.slug))
                      .map((sub) => (
                        <option key={sub.slug} value={sub.slug}>
                          {sub.name}
                        </option>
                      ))}
                  </select>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label className={labelClass} htmlFor="business-district">
            District *
          </label>
          <select id="business-district" name="district" required defaultValue="" className={selectClass}>
            <option value="" disabled>
              Select district
            </option>
            {districts.map((district) => (
              <option key={district.slug} value={district.name}>
                {district.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor="business-town">
            Town / city *
          </label>
          <input
            id="business-town"
            name="town"
            type="text"
            required
            placeholder="e.g. Udupi"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="business-address">
            Full address *
          </label>
          <input
            id="business-address"
            name="address"
            type="text"
            required
            autoComplete="street-address"
            placeholder="Shop / building, street, area, PIN code"
            className={inputClass}
          />
        </div>
      </div>

      {/* ── Video tour (Premium only) ─────────────────────────────────────────── */}
      {limits.video ? (
        <div>
          <label className={labelClass} htmlFor="business-video">
            Video tour link
          </label>
          <input
            id="business-video"
            name="video_url"
            type="url"
            placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
            className={inputClass}
          />
          <p className="mt-1 text-xs text-foreground-500">
            Included with your {planLabel} plan — paste a YouTube, Vimeo or other streaming link. It
            appears as a “Video Tour” on your business listing page.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-xs text-foreground-600">
          <i className="ri-lock-line text-secondary-600" />
          Video tour isn’t included in your {planLabel} plan.
          {videoPlan ? (
            <button
              type="button"
              onClick={() => selectPlan(videoPlan.slug)}
              className="cursor-pointer font-semibold text-primary-700 hover:underline"
            >
              Switch to {videoPlan.name}
            </button>
          ) : null}
        </div>
      )}

      <div>
        <label className={labelClass} htmlFor="business-description">
          About your business *
        </label>
        <textarea
          id="business-description"
          name="description"
          required
          maxLength={500}
          rows={5}
          placeholder="Tell customers what you offer, what makes you different and your specialties..."
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
        <p className="mt-1 text-xs text-foreground-500">Maximum 500 characters.</p>
      </div>

      {/* Reserved anti-spam field — kept out of view and never required. */}
      <input
        type="text"
        name={HONEYPOT}
        className="field-guard-offscreen"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" ? (
        <FormFeedback status="error" title="Could not submit your listing" message={errorMessage} />
      ) : null}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin text-base" : "ri-send-plane-line text-base"} />
        </span>
        {status === "submitting" ? "Submitting..." : "Submit my business"}
      </button>
    </form>
  );
}